import { useTranslation } from 'react-i18next';
import BucketDots from './BucketDots';
import DeletedBadge from './DeletedBadge';
import RefreshButton from './RefreshButton';
// Hidden now that Tag It and Rate It are separate Insights modules; kept for switch-back.
// import TiriModuleBadges from './TiriModuleBadges';

const MODULE_LABELS = {
  fancam: 'FanCam',
  tiri: 'TIRI',
  tagit: 'TIRI – Tag It',
  rateit: 'TIRI – Rate It',
  fanwave: 'FanWave',
  goalrush: 'GoalRush',
  replay: 'P4P Replay',
  rewards: 'Rewards',
  predictions: 'Predictions',
  quizzes: 'Quizzes',
  polls: 'Polls',
  ptw: 'GloryBoard',
  fanchat: 'FanChat',
  playerStats: 'Game Stat'
};

const fmtNumber = (n) => (n || 0).toLocaleString();

// Values only — labels resolve from `admin.insights.items.sort.<value>` at render, so a module
// constant can't freeze them at the language active when this file loaded.
const SORT_OPTIONS = ['reach', 'events', 'recent'];

const selectClass =
  'h-8 px-2 rounded-lg border border-[#CBD5E1] bg-white text-[12px] text-[#334155] outline-none';

const fmtRelative = (iso, t) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return t('admin.insights.items.relative.justNow');
  if (mins < 60) return t('admin.insights.items.relative.minutes', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('admin.insights.items.relative.hours', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t('admin.insights.items.relative.days', { count: days });
  return new Date(iso).toLocaleDateString();
};

// Same split as the FanChat strip's median first response in `Insights/index.js`, which imports
// this file and so cannot be imported back.
const fmtFirstResponse = (sec, t) => {
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return t('admin.insights.fanchatTopics.duration.hours', { h, m });
  if (m) return t('admin.insights.fanchatTopics.duration.minutes', { m, s });
  return t('admin.insights.fanchatTopics.duration.seconds', { s });
};

/** One muted line for a FanChat 2.0 topic row: what the topic is and how it did in the period. */
const topicLine = (topic, t) =>
  [
    t(`admin.insights.fanchatTopics.topic.status.${topic.status}`, { defaultValue: topic.status }),
    topic.creator && t('admin.insights.fanchatTopics.topic.by', { name: topic.creator }),
    topic.publishedAt &&
      t('admin.insights.fanchatTopics.topic.published', {
        date: new Date(topic.publishedAt).toLocaleDateString()
      }),
    t('admin.insights.fanchatTopics.topic.membersNow', { count: topic.membersNow }),
    topic.messages != null &&
      t('admin.insights.fanchatTopics.topic.messages', { count: topic.messages }),
    topic.previews != null &&
      t('admin.insights.fanchatTopics.topic.previews', { count: topic.previews }),
    topic.joins != null && t('admin.insights.fanchatTopics.topic.joins', { count: topic.joins }),
    topic.firstResponseSec != null &&
      t('admin.insights.fanchatTopics.topic.firstResponse', {
        time: fmtFirstResponse(topic.firstResponseSec, t)
      })
  ]
    .filter(Boolean)
    .join(' · ');

const ItemThumbnail = ({ thumbnail, title, deleted }) =>
  thumbnail ? (
    // Lazy + async decode: a thumbnail can be the full-size photo (the upload falls back to it
    // when no thumbnail was made), and decoding a page of those on the main thread is what made
    // scrolling stutter. The explicit size lets the browser decode at 40px, not full resolution.
    <img
      src={thumbnail}
      alt=""
      width={40}
      height={40}
      loading="lazy"
      decoding="async"
      className={`h-10 w-10 shrink-0 rounded-lg object-cover bg-[#F1F5F9] ${deleted ? 'opacity-40 grayscale' : ''}`}
    />
  ) : (
    <div
      className={`h-10 w-10 shrink-0 rounded-lg bg-[#EEF2FF] flex items-center justify-center text-[#3B5BDB] text-[14px] font-semibold ${deleted ? 'opacity-40' : ''}`}
    >
      {(title || '?').charAt(0).toUpperCase()}
    </div>
  );

const ModuleItemsList = ({
  module,
  items,
  total,
  loading,
  page,
  pageSize,
  onPageChange,
  onClose,
  onItemClick,
  onRefresh,
  refreshing,
  sort,
  onSortChange
}) => {
  const { t } = useTranslation();
  const moduleLabel = MODULE_LABELS[module] || module;
  const hasPrev = page > 0;
  const hasNext = (page + 1) * pageSize < total;
  const rangeStart = total === 0 ? 0 : page * pageSize + 1;
  const rangeEnd = Math.min((page + 1) * pageSize, total);

  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <div>
          <p className="text-[13px] font-semibold text-[#0F172A]">
            {t('admin.insights.items.title', { module: moduleLabel })}
          </p>
          <p className="text-[11px] text-[#94A3B8]">{t(`admin.insights.items.sortHint.${sort}`)}</p>
          {module === 'playerStats' && (
            <p className="text-[11px] text-[#94A3B8] mt-0.5">
              {t('admin.insights.items.playerStatsNote')}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            className={selectClass}
            aria-label={t('admin.insights.items.sort.label')}
          >
            {SORT_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {t(`admin.insights.items.sort.${value}`)}
              </option>
            ))}
          </select>
          {/* Refreshes THIS page of items, keeping the current page and filter — an admin
              watching one module's items should not be bounced back to page 1 to see whether a
              count moved. */}
          <RefreshButton
            onClick={onRefresh}
            refreshing={refreshing}
            disabled={loading}
            title={t('admin.insights.items.refresh', { module: moduleLabel })}
          />
          <button
            onClick={onClose}
            className="text-[12px] font-medium text-[#64748B] hover:text-[#334155] transition-colors md:hidden"
          >
            {t('admin.insights.items.close')}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-5 space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="h-10 w-10 rounded-lg bg-[#F1F5F9]" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/2 bg-[#F1F5F9] rounded" />
                <div className="h-2.5 w-1/3 bg-[#F1F5F9] rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : !items?.length ? (
        <div className="p-10 text-center">
          <p className="text-[13px] font-medium text-[#334155]">
            {t('admin.insights.items.empty')}
          </p>
          <p className="text-[12px] text-[#94A3B8] mt-1">
            {t('admin.insights.items.emptySub', { module: moduleLabel })}
          </p>
        </div>
      ) : (
        <>
          <div className="divide-y divide-[#F1F5F9]">
            {items.map((row) => (
              <button
                key={row.itemId}
                onClick={() => onItemClick?.(row)}
                className="w-full flex items-center gap-3 px-5 py-3.5 text-left hover:bg-[#F8FAFC] transition-colors"
              >
                <ItemThumbnail thumbnail={row.thumbnail} title={row.title} deleted={row.deleted} />

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-[13px] font-medium text-[#0F172A] truncate">{row.title}</p>
                    {/* <TiriModuleBadges modules={row.modules} className="shrink-0" /> */}
                    <DeletedBadge deleted={row.deleted} className="shrink-0" />
                  </div>
                  {row.subtitle && (
                    <p className="text-[11px] text-[#94A3B8] truncate">
                      {/* A GoalRush session's subtitle is its raw end reason (or `active`). */}
                      {module === 'goalrush'
                        ? t(`admin.insights.goalrushStats.endReason.${row.subtitle}`, {
                            defaultValue: row.subtitle
                          })
                        : row.subtitle}
                    </p>
                  )}
                  {row.topic && (
                    <p className="text-[11px] text-[#64748B] truncate">{topicLine(row.topic, t)}</p>
                  )}
                  <BucketDots row={row} />
                </div>

                <div className="shrink-0 text-right">
                  <p className="text-[16px] font-semibold text-[#0F172A] tabular-nums">
                    {fmtNumber(row.total)}
                  </p>
                  <p className="text-[10px] text-[#94A3B8]">
                    {t('admin.insights.items.events', { count: row.total })}
                  </p>
                  {row.lastEventAt && (
                    <p
                      className="text-[10px] text-[#94A3B8]"
                      title={new Date(row.lastEventAt).toLocaleString()}
                    >
                      {fmtRelative(row.lastEventAt, t)}
                    </p>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between px-5 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC]">
            <p className="text-[12px] text-[#94A3B8]">
              {t('admin.insights.items.range', {
                start: rangeStart,
                end: rangeEnd,
                total: fmtNumber(total)
              })}
            </p>
            <div className="flex gap-2">
              <button
                disabled={!hasPrev}
                onClick={() => onPageChange(page - 1)}
                className="h-8 px-3 rounded-lg text-[12px] font-medium border border-[#CBD5E1] bg-white text-[#334155] hover:border-[#94A3B8] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#CBD5E1] transition-colors"
              >
                {t('admin.insights.items.prev')}
              </button>
              <button
                disabled={!hasNext}
                onClick={() => onPageChange(page + 1)}
                className="h-8 px-3 rounded-lg text-[12px] font-medium border border-[#CBD5E1] bg-white text-[#334155] hover:border-[#94A3B8] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[#CBD5E1] transition-colors"
              >
                {t('admin.insights.items.next')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ModuleItemsList;
